import type * as React from "react";
import { cn } from "@/lib/utils";

/** A labelled form control with a hint below it; `invalid` turns the hint into the error message. */
function Field({ label, hint, invalid, required, className, children }: { label: React.ReactNode; hint?: React.ReactNode; invalid?: boolean; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <label data-slot="field" className={cn("block min-w-0", className)}>
      <span className="mb-1.5 block font-semibold">
        {label}
        {required && <span className="font-normal text-danger"> *</span>}
      </span>
      {children}
      {hint && <small className={cn("mt-1.5 block text-xs text-muted-foreground", invalid && "text-danger")}>{hint}</small>}
    </label>
  );
}

/** Columns at least 240px wide; hints of different lengths do not shift the next row. */
function FieldGrid({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="field-grid" className={cn("mb-4 grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] items-start gap-x-6 gap-y-4", className)} {...props} />;
}

/** One field per row, for forms that read top to bottom. */
function FieldStack({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="field-stack" className={cn("flex flex-col gap-4", className)} {...props} />;
}

function FieldSection({ title, className, children }: { title: string; className?: string; children: React.ReactNode }) {
  return (
    <div data-slot="field-section" className={cn("[[data-slot=field-section]+&]:mt-5", className)}>
      <h3 className="mb-3 text-sm">{title}</h3>
      {children}
    </div>
  );
}

/** Numbered steps joined by a rule down the left, as in GitHub's "new repository" form. */
function FormSteps({ className, ...props }: React.ComponentProps<"ol">) {
  return <ol data-slot="form-steps" className={cn("flex flex-col", className)} {...props} />;
}

function FormStep({ n, title, className, children }: { n: number; title: React.ReactNode; className?: string; children: React.ReactNode }) {
  return (
    <li data-slot="form-step" className={cn("relative pb-8 pl-10 before:absolute before:top-7 before:bottom-0 before:left-3 before:w-px before:bg-border-muted", className)}>
      <span aria-hidden className="absolute top-0 left-0 grid size-6 place-items-center rounded-full border bg-background text-xs text-muted-foreground tabular-nums">
        {n}
      </span>
      <h2 className="mb-4 text-base leading-6 font-semibold">{title}</h2>
      {children}
    </li>
  );
}

/** A bordered box of setting rows; consecutive boxes are spaced apart. */
function FieldRows({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="field-rows" className={cn("rounded-md border [[data-slot=field-rows]+&]:mt-4", className)} {...props} />;
}

/** One setting: title and description on the left, its control on the right; `children` render full width below. */
function FieldRow({ title, description, control, className, children }: { title: React.ReactNode; description?: React.ReactNode; control?: React.ReactNode; className?: string; children?: React.ReactNode }) {
  return (
    <div data-slot="field-row" className={cn("px-4 py-3 [[data-slot=field-row]+&]:border-t", className)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-[1_1_240px]">
          <div className="font-semibold">{title}</div>
          {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
        </div>
        {control && <div className="shrink-0">{control}</div>}
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

export { Field, FieldGrid, FieldRow, FieldRows, FieldSection, FieldStack, FormStep, FormSteps };
